import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  marketplaceMSIBibleApi,
  type AIInteractionStage,
  type AutomationPipeline,
  type EventPropagationStep,
  type ExceptionWorkflow,
  type InteractionLink,
  type MarketplaceJourney,
  type MSIBibleStatus,
  type MSIDashboard,
  type NavigationEdge,
  type OrchestrationType,
  type PerformanceTarget,
  type UserJourneyMap,
  type WorkflowValidation,
} from '../../api/marketplace-msi-bible';

type Tab = 'overview' | 'journeys' | 'interactions' | 'personas' | 'orchestration' | 'events' | 'automation' | 'performance' | 'status';
type Theme = 'light' | 'dark';

export function MarketplaceMSIBiblePage() {
  const [theme, setTheme] = useState<Theme>('light');
  const dark = theme === 'dark';
  const [tab, setTab] = useState<Tab>('overview');
  const [dashboard, setDashboard] = useState<MSIDashboard | null>(null);
  const [journeys, setJourneys] = useState<MarketplaceJourney[]>([]);
  const [interactions, setInteractions] = useState<InteractionLink[]>([]);
  const [userJourneys, setUserJourneys] = useState<UserJourneyMap[]>([]);
  const [orchestration, setOrchestration] = useState<OrchestrationType[]>([]);
  const [eventFlow, setEventFlow] = useState<EventPropagationStep[]>([]);
  const [aiStages, setAiStages] = useState<AIInteractionStage[]>([]);
  const [navEdges, setNavEdges] = useState<NavigationEdge[]>([]);
  const [pipelines, setPipelines] = useState<AutomationPipeline[]>([]);
  const [exceptions, setExceptions] = useState<ExceptionWorkflow[]>([]);
  const [perfTargets, setPerfTargets] = useState<PerformanceTarget[]>([]);
  const [status, setStatus] = useState<MSIBibleStatus | null>(null);
  const [wfResult, setWfResult] = useState<WorkflowValidation | null>(null);
  const [actionMsg, setActionMsg] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  const refresh = useCallback(async () => {
    setError(null);
    try {
      const [dash, j, i, u, o, e, ai, nav, pipe, ex, perf, st] = await Promise.all([
        marketplaceMSIBibleApi.dashboard(),
        marketplaceMSIBibleApi.marketplaceJourneys(),
        marketplaceMSIBibleApi.interactionMatrix(),
        marketplaceMSIBibleApi.userJourneys(),
        marketplaceMSIBibleApi.orchestration(),
        marketplaceMSIBibleApi.eventPropagation(),
        marketplaceMSIBibleApi.aiInteractionModel(),
        marketplaceMSIBibleApi.navigationGraph(),
        marketplaceMSIBibleApi.automationPipelines(),
        marketplaceMSIBibleApi.exceptionHandling(),
        marketplaceMSIBibleApi.performanceTargets(),
        marketplaceMSIBibleApi.status(),
      ]);
      setDashboard(dash);
      setJourneys(j);
      setInteractions(i);
      setUserJourneys(u);
      setOrchestration(o);
      setEventFlow(e);
      setAiStages(ai);
      setNavEdges(nav);
      setPipelines(pipe);
      setExceptions(ex);
      setPerfTargets(perf);
      setStatus(st);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load MSI bible');
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  const validateWorkflow = async () => {
    setBusy(true);
    setActionMsg(null);
    try {
      const result = await marketplaceMSIBibleApi.validateWorkflow({
        workflow_key: 'listing_optimization',
        attrs: { modules_connected: true, events_defined: true, journey_mapped: true },
      });
      setWfResult(result);
      setActionMsg(result.valid ? 'Workflow passes MIB-000006 connectivity validation' : `Gaps: ${result.gaps?.join(', ')}`);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Validation failed');
    } finally {
      setBusy(false);
    }
  };

  const card = dark ? 'border-slate-800 bg-slate-900' : 'border-slate-200 bg-white';
  const muted = dark ? 'text-slate-400' : 'text-slate-500';
  const shell = dark ? 'bg-slate-950 text-slate-100' : 'bg-white text-slate-900';
  const accent = 'text-sky-500';
  const tabActive = 'bg-sky-600 text-white';

  const tabs: { id: Tab; label: string }[] = [
    { id: 'overview', label: 'Executive Summary' },
    { id: 'journeys', label: 'Marketplace Journeys' },
    { id: 'interactions', label: 'Interaction Matrix' },
    { id: 'personas', label: 'User Journeys' },
    { id: 'orchestration', label: 'Orchestration & AI' },
    { id: 'events', label: 'Events & Navigation' },
    { id: 'automation', label: 'Automation & Exceptions' },
    { id: 'performance', label: 'Performance Targets' },
    { id: 'status', label: 'Bible Status' },
  ];

  return (
    <div className={`space-y-6 rounded-xl p-1 ${shell}`}>
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <p className={`text-xs font-semibold uppercase tracking-wide ${accent}`}>MIB-000006</p>
          <h1 className="text-2xl font-semibold">Marketplace System Interaction & Workflow Architecture</h1>
          <p className={`text-sm ${muted}`}>
            End-to-end workflow blueprint, cross-module interaction model, event propagation and product orchestration
          </p>
          <div className="mt-2 flex flex-wrap gap-2 text-xs">
            {[1, 2, 3, 4, 5].map((n) => (
              <Link key={n} to={`/platform/marketplace-${n === 1 ? 'intelligence' : n === 2 ? 'kpi' : n === 3 ? 'dna' : n === 4 ? 'growth-experiment' : 'mos'}-bible`} className={`${accent} hover:underline`}>
                MIB-00000{n}
              </Link>
            ))}
          </div>
        </div>
        <button type="button" onClick={() => setTheme(dark ? 'light' : 'dark')} className={`rounded-lg border px-3 py-1.5 text-sm ${card}`}>
          {dark ? 'Light mode' : 'Dark mode'}
        </button>
      </div>

      {error && <div className="rounded-lg border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-950 dark:text-red-300">{error}</div>}
      {actionMsg && <div className="rounded-lg border border-emerald-300 bg-emerald-50 p-3 text-sm text-emerald-700 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-300">{actionMsg}</div>}

      <div className="flex flex-wrap gap-2 border-b border-slate-200 pb-2 dark:border-slate-800">
        {tabs.map((t) => (
          <button key={t.id} type="button" onClick={() => setTab(t.id)} className={`rounded-lg px-3 py-1.5 text-sm ${tab === t.id ? tabActive : `${card} ${muted}`}`}>
            {t.label}
          </button>
        ))}
      </div>

      {tab === 'overview' && dashboard && (
        <div className="space-y-4">
          <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-4">
            {[
              { label: 'Bible ID', value: dashboard.bible_id },
              { label: 'Marketplace Journeys', value: dashboard.marketplace_journeys },
              { label: 'Interaction Links', value: dashboard.interaction_links },
              { label: 'User Journey Maps', value: dashboard.user_journey_maps },
              { label: 'Orchestration Types', value: dashboard.orchestration_types },
              { label: 'Event Flows', value: dashboard.event_propagation_flows },
              { label: 'Automation Pipelines', value: dashboard.automation_pipelines },
              { label: 'Performance Targets', value: dashboard.performance_targets },
            ].map((item) => (
              <div key={item.label} className={`rounded-xl border p-4 ${card}`}>
                <p className={`text-xs uppercase ${muted}`}>{item.label}</p>
                <p className="mt-1 text-2xl font-semibold">{item.value}</p>
              </div>
            ))}
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Engineering Blueprint — Final Architecture Before Production</h3>
            <p className={`mt-2 text-sm ${muted}`}>
              Every module, workflow, business event and user interaction is fully connected. One unified interaction model governs all production development.
            </p>
          </div>
        </div>
      )}

      {tab === 'journeys' && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h3 className="font-medium">End-to-End Marketplace Journeys</h3>
          <div className="mt-3 grid gap-3 md:grid-cols-2">
            {journeys.map((j) => (
              <div key={j.key} className="rounded-lg border p-4 dark:border-slate-700">
                <div className="flex items-center justify-between">
                  <span className="font-medium">{j.name}</span>
                  <span className={`rounded px-2 py-0.5 text-xs ${muted}`}>{j.phase}</span>
                </div>
                <ol className="mt-2 list-decimal pl-5 text-sm">
                  {j.steps.map((s) => <li key={s}>{s}</li>)}
                </ol>
                {j.modules && (
                  <div className="mt-2 flex flex-wrap gap-1">
                    {j.modules.map((m) => <span key={m} className="rounded bg-sky-100 px-2 py-0.5 text-xs font-mono dark:bg-sky-900">{m}</span>)}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}

      {tab === 'interactions' && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h3 className="font-medium">Cross-Module Interaction Matrix</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${muted}`}>
                  <th className="p-2 text-left">Source</th>
                  <th className="p-2 text-left">Target</th>
                  <th className="p-2 text-left">Trigger</th>
                  <th className="p-2 text-left">Effect</th>
                  <th className="p-2 text-left">Event</th>
                </tr>
              </thead>
              <tbody>
                {interactions.map((l, idx) => (
                  <tr key={idx} className="border-b dark:border-slate-700">
                    <td className="p-2 font-mono text-xs">{l.source_module}</td>
                    <td className="p-2 font-mono text-xs">{l.target_module}</td>
                    <td className="p-2">{l.trigger}</td>
                    <td className="p-2">{l.downstream_effect}</td>
                    <td className="p-2 font-mono text-xs text-sky-500">{l.event}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'personas' && (
        <div className="grid gap-4 md:grid-cols-2">
          {userJourneys.map((u) => (
            <div key={u.key} className={`rounded-xl border p-4 ${card}`}>
              <h4 className="font-medium">{u.persona}</h4>
              <p className={`text-sm ${muted}`}>Entry: {u.entry_point}</p>
              <p className="mt-2 text-sm font-medium">Goals</p>
              <ul className="list-disc pl-5 text-sm">{u.primary_goals.map((g) => <li key={g}>{g}</li>)}</ul>
              <p className="mt-2 text-sm font-medium">Key Screens</p>
              <div className="mt-1 flex flex-wrap gap-1">
                {u.key_screens.map((s) => <span key={s} className="rounded bg-sky-100 px-2 py-0.5 text-xs dark:bg-sky-900">{s}</span>)}
              </div>
            </div>
          ))}
        </div>
      )}

      {tab === 'orchestration' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Workflow Orchestration Types</h3>
            <div className="mt-3 space-y-3">
              {orchestration.map((o) => (
                <div key={o.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{o.name}</span>
                    <span className="rounded bg-sky-100 px-2 py-0.5 text-xs dark:bg-sky-900">{o.automation_level}</span>
                  </div>
                  <p className={`text-sm ${muted}`}>{o.description}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">AI Interaction Model</h3>
            <div className="mt-3 space-y-2">
              {aiStages.map((s) => (
                <div key={s.key} className="flex items-center gap-3 rounded border p-2 dark:border-slate-700">
                  <span className="font-mono text-xs text-sky-500">{s.order}</span>
                  <div>
                    <span className="font-medium">{s.name}</span>
                    {s.description && <p className={`text-xs ${muted}`}>{s.description}</p>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'events' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Event Propagation Flow</h3>
            <div className="mt-3 space-y-2">
              {eventFlow.map((s) => (
                <div key={s.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs text-sky-500">{s.order}</span>
                    <span className="font-mono text-sm">{s.event}</span>
                  </div>
                  <p className={`mt-1 text-sm ${muted}`}>Consumers: {s.consumers.join(', ')}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Navigation Graph</h3>
            <div className="mt-3 space-y-2">
              {navEdges.map((e, idx) => (
                <div key={idx} className="rounded border p-2 text-sm dark:border-slate-700">
                  <span className="font-mono">{e.from}</span>
                  <span className={`mx-2 ${muted}`}>→</span>
                  <span className="font-mono">{e.to}</span>
                  <span className={`ml-2 text-xs ${muted}`}>({e.transition})</span>
                  {e.preserves_context && <span className="ml-2 text-xs text-emerald-500">context preserved</span>}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {tab === 'automation' && (
        <div className="grid gap-4 lg:grid-cols-2">
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Automation Pipelines</h3>
            <div className="mt-3 space-y-3">
              {pipelines.map((p) => (
                <div key={p.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <span className="font-medium">{p.name}</span>
                  <p className={`text-sm ${muted}`}>{p.description}</p>
                  <p className="mt-1 text-xs">Triggers: {p.triggers.join(', ')}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Exception Handling</h3>
            <div className="mt-3 space-y-3">
              {exceptions.map((e) => (
                <div key={e.key} className="rounded-lg border p-3 dark:border-slate-700">
                  <div className="flex items-center justify-between">
                    <span className="font-medium">{e.name}</span>
                    {e.supports_rollback && <span className="text-xs text-amber-500">rollback</span>}
                  </div>
                  <p className={`text-sm ${muted}`}>{e.scenario}</p>
                </div>
              ))}
            </div>
          </div>
          <div className={`col-span-full rounded-xl border p-4 ${card}`}>
            <h3 className="font-medium">Validate Workflow Connectivity</h3>
            <button type="button" disabled={busy} onClick={validateWorkflow} className="mt-3 rounded-lg bg-sky-600 px-4 py-2 text-sm text-white disabled:opacity-50">
              {busy ? 'Validating…' : 'Validate Listing Optimization Workflow'}
            </button>
            {wfResult && (
              <div className="mt-4 grid gap-2 md:grid-cols-3">
                {[
                  { label: 'Valid', value: wfResult.valid ? 'Yes' : 'No' },
                  { label: 'Modules Connected', value: wfResult.modules_connected ? 'Yes' : 'No' },
                  { label: 'Events Defined', value: wfResult.events_defined ? 'Yes' : 'No' },
                ].map((item) => (
                  <div key={item.label} className="rounded-lg border p-3 dark:border-slate-700">
                    <p className={`text-xs ${muted}`}>{item.label}</p>
                    <p className="font-semibold">{item.value}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {tab === 'performance' && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h3 className="font-medium">Performance Targets</h3>
          <div className="mt-3 overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${muted}`}>
                  <th className="p-2 text-left">Surface</th>
                  <th className="p-2 text-right">Target (ms)</th>
                  <th className="p-2 text-right">P95 (ms)</th>
                  <th className="p-2 text-left">Description</th>
                </tr>
              </thead>
              <tbody>
                {perfTargets.map((t) => (
                  <tr key={t.key} className="border-b dark:border-slate-700">
                    <td className="p-2 font-medium">{t.surface}</td>
                    <td className="p-2 text-right font-mono">{t.target_ms}</td>
                    <td className="p-2 text-right font-mono">{t.p95_ms ?? '—'}</td>
                    <td className={`p-2 ${muted}`}>{t.description}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {tab === 'status' && status && (
        <div className={`rounded-xl border p-4 ${card}`}>
          <h3 className="font-medium">MIB-000006 Bible Status</h3>
          <dl className="mt-4 grid gap-3 md:grid-cols-2">
            {Object.entries(status).map(([k, v]) => (
              <div key={k}>
                <dt className={`text-xs uppercase ${muted}`}>{k.replace(/_/g, ' ')}</dt>
                <dd className="font-medium">{Array.isArray(v) ? v.join(', ') : String(v)}</dd>
              </div>
            ))}
          </dl>
        </div>
      )}
    </div>
  );
}
